import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight, Calendar, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * MagazineSpotlight — replica il design di /rivista con:
 * 1. Hero card grande con cover corrente + "QUARTERLY MAGAZINE" e "N°X · STAGIONE"
 * 2. Griglia "I numeri 2026" con 4 numeri stagionali
 * Consuma /api/magazine/issues (backend legacy con cover static reali)
 */
export default function MagazineSpotlight() {
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    axios.get(`${API}/magazine/issues`).then((r) => setIssues(r.data.issues || [])).catch(() => {});
  }, []);

  if (issues.length === 0) return null;

  const featured = issues.find((i) => i.downloadable) || issues[0];
  const featuredCover = resolveCover(featured.cover);
  const featuredNumber = issueNumber(featured.id);

  return (
    <section id="magazine" data-testid="magazine-spotlight" className="py-24 md:py-32 bg-[#faf7f0]">
      <div className="container-xl">
        {/* ============ HERO CARD ============ */}
        <Link
          to="/rivista"
          data-testid="magazine-hero-card"
          className="relative block overflow-hidden bg-[#2b1810] rounded-sm shadow-2xl group"
        >
          <img
            src={featuredCover}
            alt={featured.title}
            className="absolute inset-0 w-full h-full object-cover opacity-95 transition-transform duration-[2s] group-hover:scale-105"
          />
          {/* Left dark gradient for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/10" />

          <div className="relative grid md:grid-cols-[1.15fr_1fr] gap-8 min-h-[440px] md:min-h-[520px]">
            {/* LEFT — text */}
            <div className="flex flex-col justify-between p-8 md:p-14 lg:p-16 text-white">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-white/80 font-semibold">
                <BookOpen className="h-3.5 w-3.5" />
                Quarterly Magazine
              </div>

              <div className="mt-auto">
                <h2 className="editorial-heading text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                  Amazonico Magazine<br />
                  <span className="italic text-[#C9A961]">Magazine.</span>
                </h2>
                <p className="mt-6 text-white/80 text-base md:text-lg max-w-md leading-relaxed">
                  La rivista trimestrale di vino, cucina e cultura italiana.
                </p>
                <div className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] font-bold text-white group-hover:text-[#C9A961] transition-colors">
                  Leggi i numeri <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>

            {/* RIGHT — badge top-right */}
            <div className="hidden md:block relative">
              <div className="absolute top-8 right-8 px-4 py-1.5 bg-[#C9A961] text-white text-[10px] uppercase tracking-[0.25em] font-bold shadow-lg">
                {featuredNumber}
              </div>
            </div>
          </div>
        </Link>

        {/* ============ ISSUES 2026 GRID ============ */}
        <div className="mt-16 md:mt-20">
          <h2 className="editorial-heading text-4xl md:text-5xl text-stone-900 mb-10">
            I numeri 2026
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="magazine-issues-grid">
            {issues.map((it) => (
              <IssueCard key={it.id} it={it} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function IssueCard({ it }) {
  const cover = resolveCover(it.cover);
  const dateLabel = new Date(it.published_at).toLocaleDateString("it-IT", { month: "long", year: "numeric" }).toUpperCase();
  const linkTo = it.downloadable ? `/rivista/${it.id}` : "/rivista";
  return (
    <Link
      to={linkTo}
      data-testid={`magazine-issue-${it.id}`}
      className="group block overflow-hidden bg-white rounded-sm shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-stone-100">
        <img
          src={cover}
          alt={it.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105"
          loading="lazy"
        />
        {/* Bottom gradient for text */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Season badge top-left */}
        <div className="absolute top-4 left-4 px-3 py-1 bg-[#C9A961] text-white text-[10px] uppercase tracking-[0.2em] font-bold shadow">
          {it.season}
        </div>

        {/* "IN ARRIVO" top-right if not downloadable */}
        {!it.downloadable && (
          <div className="absolute top-4 right-4 px-2.5 py-1 bg-white/90 backdrop-blur text-stone-700 text-[9px] uppercase tracking-[0.2em] font-bold flex items-center gap-1 shadow">
            <Lock className="h-2.5 w-2.5" /> In arrivo
          </div>
        )}
      </div>

      {/* Bottom text block */}
      <div className="p-5">
        <h3 className="editorial-heading text-xl md:text-2xl text-stone-900 leading-tight">{it.title}</h3>
        <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold flex items-center gap-1.5">
          <Calendar className="h-3 w-3 text-[#C9A961]" />
          {dateLabel} · {it.pages} PAGINE
        </div>
      </div>
    </Link>
  );
}

function resolveCover(cover) {
  if (!cover) return "";
  return cover.startsWith("/api/") ? `${BACKEND}${cover}` : cover;
}

function issueNumber(id) {
  // Extract N°1..4 from season order (winter→1, spring→2, summer→3, autumn→4)
  const map = { winter: "N°1 · INVERNO", spring: "N°2 · PRIMAVERA", summer: "N°3 · ESTATE", autumn: "N°4 · AUTUNNO" };
  for (const k of Object.keys(map)) {
    if (id.startsWith(k)) return map[k];
  }
  return "N°1 · INVERNO";
}
